import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useLocation } from "react-router-dom";

import { OrganisationTypeConstants } from "../../../constants";
import { CursorChangeProps, DataGridColumnCurrency, FileType, ResultType, SelectData } from "../../../models";
import { IStateOrigin } from "../../../navigators/navigateStateObjects";
import {
  getAssetManagerDetails,
  searchPipelineDeals,
  SearchPipelineDealsRequest,
  SearchPipelineDealsResponse,
} from "../../../service/query";
import { getPipelineAggregate, getPortfolio } from "../../../service/query/QueryService.full";
import { FilterGroups, ResultData, Status } from "../../../service/Shared";
import { useAuth } from "../../../useAuth";
import { flattenObject, formatNumber, thousandsFormatterToLower } from "../../../utils";
import { getDealListingRoute } from "../../../utils/routes";
import {
  ChartData,
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  DataGridLinkCellFormatterData,
  dataGridMapFilterCriteria,
} from "../../../widget";

interface FundDetailsInterface {
  name: string | undefined;
  currentSize: string | undefined;
  targetSize: string | undefined;
  targetIrr: string | undefined;
}

interface UseDashboardReturnData {
  refresh: boolean;
  isLoading: boolean;
  isLoadingLatestDeals: boolean;
  bannerUrl: string | null | undefined;
  firstName: string | undefined;
  dealTypeChartData: ChartData;
  dealStageChartData: ChartData;
  dealTypeOrStage: string;
  fundDetails: FundDetailsInterface;
  tableColumnsLatestDeals: DataGridColumnDefinition[];
  pipelinesData: SelectData;
  selectedPipeline: string;
  defaultSortingCriteria: SortColumn[];
  setDealTypeOrStage: Dispatch<SetStateAction<string>>;
  onLatestDealsDataLoad: DataGridCursorDataLoadEventHandler;
  handleChangePipelineFilter: (pipeline: string) => void;
  hasPermission: (permission: string) => boolean;
}

const defaultSortingCriteria: SortColumn[] = [{ columnKey: "createdAt", direction: "DESC" }];

export const useDashboard = (): UseDashboardReturnData => {
  const { hasPermission, currentAssetManagerUuid, currentOrganisationUuid, user } = useAuth();
  const stateLocation = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLatestDeals, setIsLoadingLatestDeals] = useState(true);
  const [bannerUrl, setBannerUrl] = useState<string | null>();

  const [dealTypeChartData, setDealTypeChartData] = useState<ChartData>({});
  const [dealStageChartData, setDealStageChartData] = useState<ChartData>({});

  const [dealTypeOrStage, setDealTypeOrStage] = useState<string>("By type");

  // TODO - Update when we have portfolio api
  // eslint-disable-next-line unused-imports/no-unused-vars
  const [fundDetails, setFundDetails] = useState<FundDetailsInterface>({
    name: undefined,
    currentSize: undefined,
    targetSize: undefined,
    targetIrr: undefined,
  });

  const [selectedPipeline, setSelectedPipeline] = useState<string>("All");
  const [pipelinesData, setPipelinesData] = useState<SelectData>([{ value: "All", key: "All" }]);
  const [refresh, setRefresh] = useState<boolean>(true);
  const tableColumnsLatestDeals: DataGridColumnDefinition[] = [
    {
      key: "dealLink",
      name: "Deal",
      dataType: "string",
      formatter: "link",
      filterable: true,
      sortable: true,
    },
    {
      name: "Opportunity",
      key: "opportunity",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: true,
      sortable: false,
    },
    {
      name: "Stage",
      key: "cachedStage",
      dataType: "string",
      formatter: "defaultPill",
      filterable: true,
      sortable: true,
    },
    {
      key: "dealTypes",
      name: "Deal types",
      dataType: "string",
      formatter: "stringWithEllipsis",
      filterable: true,
      sortable: false,
    },
    {
      key: "probability",
      name: "Probability",
      dataType: "number",
      formatter: "percentage",
      filterable: false,
      sortable: true,
    },
    {
      key: "targetReturns",
      name: "Target returns",
      dataType: "number",
      formatter: "currency",
      currency: DataGridColumnCurrency.Gbp,
      filterable: true,
      sortable: true,
    },
  ];

  const handleChangePipelineFilter = (pipeline: string): void => {
    setSelectedPipeline(pipeline);
    setRefresh((prev) => !prev);
  };

  const formatDealsData = useCallback(
    (responseData: SearchPipelineDealsResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.dealLink = {
          text: el.name,
          to: getDealListingRoute(el.uuid, OrganisationTypeConstants.ASSET_MANAGER),
          state: { origin: { goBackText: "Back to dashboard", from: stateLocation.pathname } as IStateOrigin },
        } as DataGridLinkCellFormatterData;

        result.opportunity = el.relatedObjects.map((x) => x.name).join(", ");
        result.dealTypes = el.types.map((type) => type.name).join(", ");
        result.probability = el.conversionProbability !== null ? el.conversionProbability * 100 : null;

        return result;
      }) || [],
    []
  );

  const onLatestDealsDataLoad: DataGridCursorDataLoadEventHandler = async ({
    paging,
    sorting,
    filtering,
  }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 6,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    if (currentOrganisationUuid) {
      setIsLoadingLatestDeals(true);
      const filterCriteria = dataGridMapFilterCriteria(filtering);
      const filterGroups: FilterGroups<SearchPipelineDealsRequest["filter"]>["filterGroups"] = [];

      if (selectedPipeline !== "All")
        filterCriteria.pipeline = {
          uuid: {
            operator: "eq",
            value: selectedPipeline,
          },
        };

      if (filterCriteria.opportunity) {
        filterCriteria.relatedObjects = {
          some: {
            name: filterCriteria.opportunity,
          },
        };
        delete filterCriteria.opportunity;
      }

      if (filterCriteria.dealLink) {
        filterCriteria.name = filterCriteria.dealLink;
        delete filterCriteria.dealLink;
      }

      const sortKeyMap: Record<string, string> = {
        dealLink: "name",
        probability: "conversionProbability",
      };

      const request: SearchPipelineDealsRequest & FilterGroups<SearchPipelineDealsRequest["filter"]> = {
        paging: {
          limit: 7,
          beforeCursor: null,
          afterCursor: null,
        },
        /* eslint-disable @typescript-eslint/no-explicit-any */
        sort:
          sorting.length > 0
            ? sorting.map((s: { key: any; direction: any }) => ({
                key: sortKeyMap[s.key] || s.key,
                direction: s.direction,
              }))
            : [{ key: "createdAt", direction: "DESC" }],
        organisationUuid: currentOrganisationUuid,
        filter: { results: filterCriteria },
        ...(filterGroups.length ? { filterGroups } : {}),
      };

      await searchPipelineDeals(request)
        .then((response) => {
          data = {
            resultData: formatDealsData(response.data),
            paging: {
              startCursor: response.data?.paging?.startCursor || "",
              endCursor: response.data?.paging?.endCursor || "",
              hasNextPage: false,
              hasPreviousPage: false,
              pageSize: paging.pageSize || 10,
              totalCount: response.data?.paging?.total || response.data?.results?.length || 0,
            },
          };

          setPipelinesData([
            { key: "All", value: "All" },
            ...(response.data?.aspects.pipelines.map((x) => ({ key: x.uuid, value: x.name })) as SelectData),
          ]);
        })
        .finally(() => {
          setIsLoadingLatestDeals(false);
        });
    }
    return data;
  };

  const fetchData = useCallback(async (assetManagerUuid: string, organisationUuid: string) => {
    const assetManagerDetailsRes = await getAssetManagerDetails({ assetManagerUuid });
    if (assetManagerDetailsRes.status === Status.Success && assetManagerDetailsRes.data)
      setBannerUrl(assetManagerDetailsRes.data?.files.find((f) => f.type === FileType.Banner)?.file.url || null);

    const portfolioRes = await getPortfolio({ organisationUuid, portfolioUuid: null });
    if (portfolioRes.status === Status.Success && portfolioRes.data)
      setFundDetails({
        targetSize: `£${thousandsFormatterToLower(portfolioRes.data.targetSize)}`,
        targetIrr: `${formatNumber(portfolioRes.data.targetIrr * 100)}%`,
        currentSize: `£${thousandsFormatterToLower(portfolioRes.data.currentSize)}`,
        name: portfolioRes.data.fund.name,
      });

    const [dealTypeAggregateRes, dealStageAggregateRes] = await Promise.all([
      getPipelineAggregate({
        organisationUuid,
        pipelineUuid: null,
        filters: [],
        groupBy: "dealtype",
        aggregation: "dealcount",
      }),
      getPipelineAggregate({
        organisationUuid,
        pipelineUuid: null,
        filters: [],
        groupBy: "stage",
        aggregation: "dealcount",
      }),
    ]);

    if (dealTypeAggregateRes.status === Status.Success && dealTypeAggregateRes.data) {
      const dealTypeData = dealTypeAggregateRes.data.results.reduce<ChartData>((previous, current) => {
        // eslint-disable-next-line no-param-reassign
        previous[current.key] = Number(current.value);
        return previous;
      }, {});
      setDealTypeChartData(dealTypeData);
    }

    if (dealStageAggregateRes.status === Status.Success && dealStageAggregateRes.data) {
      const dealStageData = dealStageAggregateRes.data.results.reduce<ChartData>((previous, current) => {
        // eslint-disable-next-line no-param-reassign
        previous[current.key] = Number(current.value);
        return previous;
      }, {});
      setDealStageChartData(dealStageData);
    }
  }, []);

  useEffect(() => {
    if (currentAssetManagerUuid && currentOrganisationUuid)
      fetchData(currentAssetManagerUuid, currentOrganisationUuid).finally(() => {
        setIsLoading(false);
      });
  }, [currentAssetManagerUuid, currentOrganisationUuid]);

  return {
    refresh,
    isLoading,
    isLoadingLatestDeals,
    bannerUrl,
    firstName: user?.firstName,
    dealTypeChartData,
    dealStageChartData,
    dealTypeOrStage,
    fundDetails,
    tableColumnsLatestDeals,
    selectedPipeline,
    pipelinesData,
    defaultSortingCriteria,
    setDealTypeOrStage,
    onLatestDealsDataLoad,
    handleChangePipelineFilter,
    hasPermission,
  };
};
